import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

export function PrivacyPage() {
  const { i18n } = useTranslation();
  
  const getText = (fr, ar, en) => {
    if (i18n.language === 'fr') return fr;
    if (i18n.language === 'ar') return ar;
    return en;
  };

  return (
    <div data-testid="privacy-page" className="bg-[#F8F6F3] min-h-screen">
      {/* Hero */}
      <section className="bg-[#1a1a1a] py-24">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              {getText('Politique de Confidentialité', 'سياسة الخصوصية', 'Privacy Policy')}
            </h1>
            <p className="text-white/60">
              {getText(
                'Protection de vos données personnelles',
                'حماية بياناتك الشخصية',
                'Protection of your personal data'
              )}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container-main max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#1a1a1a] mb-4 flex items-center gap-3">
                <span className="w-2 h-8 bg-[#f4b00f] rounded-full"></span>
                {getText('Introduction', 'مقدمة', 'Introduction')}
              </h2>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <p className="text-gray-600 mb-4">
                  {getText(
                    "D.One SAS (ci-après « nous », « notre » ou « D.One ») s'engage à protéger la vie privée des utilisateurs de son site web. Cette politique de confidentialité explique comment nous collectons, utilisons, partageons et protégeons vos informations personnelles conformément au Règlement Général sur la Protection des Données (RGPD).",
                    "تلتزم D.One SAS بحماية خصوصية مستخدمي موقعها الإلكتروني. توضح سياسة الخصوصية هذه كيفية جمع واستخدام ومشاركة وحماية معلوماتك الشخصية.",
                    "D.One SAS is committed to protecting the privacy of users of its website. This privacy policy explains how we collect, use, share, and protect your personal information in accordance with the General Data Protection Regulation (GDPR)."
                  )}
                </p>
                <p className="text-gray-600">
                  <strong>{getText('Dernière mise à jour', 'آخر تحديث', 'Last updated')} :</strong> {getText('Février 2026', 'فبراير 2026', 'February 2026')}
                </p>
              </div>
            </div>

            {/* Responsable du traitement */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#1a1a1a] mb-4 flex items-center gap-3">
                <span className="w-2 h-8 bg-[#f4b00f] rounded-full"></span>
                {getText('Responsable du traitement', 'المسؤول عن المعالجة', 'Data Controller')}
              </h2>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <p className="text-gray-600">
                  <strong>D.One SAS</strong><br/>
                  Email : contact@simplyd.one<br/>
                  Téléphone : +33 4 84 25 29 44
                </p>
              </div>
            </div>

            {/* Données collectées */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#1a1a1a] mb-4 flex items-center gap-3">
                <span className="w-2 h-8 bg-[#f4b00f] rounded-full"></span>
                {getText('Données collectées', 'البيانات المجمعة', 'Data Collected')}
              </h2>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <p className="text-gray-600 mb-4">
                  {getText(
                    "Nous collectons les données suivantes :",
                    "نجمع البيانات التالية:",
                    "We collect the following data:"
                  )}
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>{getText('Nom et prénom', 'الاسم واللقب', 'First and last name')}</li>
                  <li>{getText('Adresse email professionnelle', 'البريد الإلكتروني المهني', 'Professional email address')}</li>
                  <li>{getText('Nom de l\'entreprise', 'اسم الشركة', 'Company name')}</li>
                  <li>{getText('Message de contact', 'رسالة الاتصال', 'Contact message')}</li>
                  <li>{getText('Données de navigation (cookies)', 'بيانات التصفح (ملفات تعريف الارتباط)', 'Navigation data (cookies)')}</li>
                </ul>
              </div>
            </div>

            {/* Finalités */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#1a1a1a] mb-4 flex items-center gap-3">
                <span className="w-2 h-8 bg-[#f4b00f] rounded-full"></span>
                {getText('Finalités du traitement', 'أغراض المعالجة', 'Purposes of Processing')}
              </h2>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <p className="text-gray-600 mb-4">
                  {getText(
                    "Vos données sont utilisées pour :",
                    "تُستخدم بياناتك من أجل:",
                    "Your data is used for:"
                  )}
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>{getText('Répondre à vos demandes de contact', 'الرد على طلبات الاتصال الخاصة بك', 'Responding to your contact requests')}</li>
                  <li>{getText('Vous informer sur nos produits et services', 'إعلامك بمنتجاتنا وخدماتنا', 'Informing you about our products and services')}</li>
                  <li>{getText('Améliorer notre site web', 'تحسين موقعنا الإلكتروني', 'Improving our website')}</li>
                  <li>{getText('Respecter nos obligations légales', 'الامتثال لالتزاماتنا القانونية', 'Complying with our legal obligations')}</li>
                </ul>
              </div>
            </div>

            {/* Base légale */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#1a1a1a] mb-4 flex items-center gap-3">
                <span className="w-2 h-8 bg-[#f4b00f] rounded-full"></span>
                {getText('Base légale', 'الأساس القانوني', 'Legal Basis')}
              </h2>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <p className="text-gray-600">
                  {getText(
                    "Le traitement de vos données repose sur votre consentement (formulaire de contact) et sur notre intérêt légitime à développer notre activité commerciale.",
                    "تستند معالجة بياناتك إلى موافقتك (نموذج الاتصال) ومصلحتنا المشروعة في تطوير نشاطنا التجاري.",
                    "The processing of your data is based on your consent (contact form) and on our legitimate interest in developing our business activity."
                  )}
                </p>
              </div>
            </div>

            {/* Conservation */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#1a1a1a] mb-4 flex items-center gap-3">
                <span className="w-2 h-8 bg-[#f4b00f] rounded-full"></span>
                {getText('Durée de conservation', 'مدة الاحتفاظ', 'Retention Period')}
              </h2>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <p className="text-gray-600">
                  {getText(
                    "Vos données sont conservées pendant une durée de 3 ans à compter de votre dernier contact avec nous, sauf obligation légale de conservation plus longue.",
                    "يتم الاحتفاظ ببياناتك لمدة 3 سنوات من آخر اتصال لك معنا، ما لم يكن هناك التزام قانوني بالاحتفاظ لفترة أطول.",
                    "Your data is kept for a period of 3 years from your last contact with us, unless there is a legal obligation to retain it longer."
                  )}
                </p>
              </div>
            </div>

            {/* Vos droits */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#1a1a1a] mb-4 flex items-center gap-3">
                <span className="w-2 h-8 bg-[#f4b00f] rounded-full"></span>
                {getText('Vos droits', 'حقوقك', 'Your Rights')}
              </h2>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <p className="text-gray-600 mb-4">
                  {getText(
                    "Conformément au RGPD, vous disposez des droits suivants :",
                    "وفقًا للائحة العامة لحماية البيانات، لديك الحقوق التالية:",
                    "In accordance with the GDPR, you have the following rights:"
                  )}
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>{getText('Droit d\'accès à vos données', 'حق الوصول إلى بياناتك', 'Right of access to your data')}</li>
                  <li>{getText('Droit de rectification', 'حق التصحيح', 'Right to rectification')}</li>
                  <li>{getText('Droit à l\'effacement (« droit à l\'oubli »)', 'حق المحو ("الحق في النسيان")', 'Right to erasure ("right to be forgotten")')}</li>
                  <li>{getText('Droit à la limitation du traitement', 'حق تقييد المعالجة', 'Right to restriction of processing')}</li>
                  <li>{getText('Droit à la portabilité des données', 'حق نقل البيانات', 'Right to data portability')}</li>
                  <li>{getText('Droit d\'opposition', 'حق الاعتراض', 'Right to object')}</li>
                </ul>
                <p className="text-gray-600 mt-4">
                  {getText(
                    "Pour exercer ces droits, contactez-nous à : contact@simplyd.one",
                    "لممارسة هذه الحقوق، اتصل بنا على: contact@simplyd.one",
                    "To exercise these rights, contact us at: contact@simplyd.one"
                  )}
                </p>
              </div>
            </div>

            {/* Cookies */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#1a1a1a] mb-4 flex items-center gap-3">
                <span className="w-2 h-8 bg-[#f4b00f] rounded-full"></span>
                {getText('Cookies', 'ملفات تعريف الارتباط', 'Cookies')}
              </h2>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <p className="text-gray-600 mb-4">
                  {getText(
                    "Notre site utilise des cookies pour améliorer votre expérience de navigation. Vous pouvez configurer votre navigateur pour refuser les cookies, mais certaines fonctionnalités du site pourraient ne pas fonctionner correctement.",
                    "يستخدم موقعنا ملفات تعريف الارتباط لتحسين تجربة التصفح الخاصة بك. يمكنك تكوين متصفحك لرفض ملفات تعريف الارتباط، ولكن بعض ميزات الموقع قد لا تعمل بشكل صحيح.",
                    "Our site uses cookies to improve your browsing experience. You can configure your browser to refuse cookies, but some site features may not work properly."
                  )}
                </p>
              </div>
            </div>

            {/* Sécurité */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#1a1a1a] mb-4 flex items-center gap-3">
                <span className="w-2 h-8 bg-[#f4b00f] rounded-full"></span>
                {getText('Sécurité', 'الأمان', 'Security')}
              </h2>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <p className="text-gray-600">
                  {getText(
                    "Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles appropriées pour protéger vos données personnelles contre tout accès non autorisé, modification, divulgation ou destruction.",
                    "ننفذ تدابير أمنية تقنية وتنظيمية مناسبة لحماية بياناتك الشخصية من أي وصول غير مصرح به أو تعديل أو إفشاء أو تدمير.",
                    "We implement appropriate technical and organizational security measures to protect your personal data against unauthorized access, modification, disclosure, or destruction."
                  )}
                </p>
              </div>
            </div>

            {/* Contact CNIL */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#1a1a1a] mb-4 flex items-center gap-3">
                <span className="w-2 h-8 bg-[#f4b00f] rounded-full"></span>
                {getText('Réclamation', 'شكوى', 'Complaint')}
              </h2>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <p className="text-gray-600">
                  {getText(
                    "Si vous estimez que le traitement de vos données personnelles constitue une violation du RGPD, vous avez le droit d'introduire une réclamation auprès de la CNIL (Commission Nationale de l'Informatique et des Libertés) : www.cnil.fr",
                    "إذا كنت تعتقد أن معالجة بياناتك الشخصية تشكل انتهاكًا للائحة العامة لحماية البيانات، فلديك الحق في تقديم شكوى إلى CNIL.",
                    "If you believe that the processing of your personal data constitutes a violation of the GDPR, you have the right to lodge a complaint with the CNIL (French Data Protection Authority): www.cnil.fr"
                  )}
                </p>
              </div>
            </div>

            {/* Copyright */}
            <div className="text-center py-8 border-t border-gray-200">
              <p className="text-gray-500">
                © 2026 D.One SAS. {getText('Tous droits réservés.', 'جميع الحقوق محفوظة.', 'All rights reserved.')}
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default PrivacyPage;
